import { GravyBinder } from "gravy-bind";
import { DemographicSetting } from "../models/demographic-setting";
import { DemographicsService } from "../services/demographics.service";
import { loopThroughElements } from "../utils/loop-through-elements";
import { MDCLinearProgress } from '@material/linear-progress';

export class DemographicsEditor {
    private rootElement: HTMLFormElement;
    private binder: GravyBinder;
    public currentView: string;
    public options: { [id: number]: DemographicSetting } = {};
    public static cssClass = 'demographic-manager';
    private static view = {
        loading: 'throbber',
        overview: 'overview',
        editor: 'editor'
    };
    private rootEngagementId: number;
    public isSaving: boolean;
    public isSaveComplete: boolean = false;
    private service: DemographicsService = new DemographicsService();
    private loader: MDCLinearProgress;

    constructor(rootElement: HTMLElement) {
        this.rootElement = rootElement as HTMLFormElement;
        this.initialize()
    }

    private initialize = async (): Promise<void> => {
        loopThroughElements(this.rootElement.querySelectorAll(`md-checkbox[type="checkbox"][data-for]`), el => {
            const idString = el.dataset.for;
            if (idString) {
                const id = Number(idString);
                if (!this.options[id]) {
                    this.options[id] = new DemographicSetting();
                }
            }
        });
        this.bindEvents();
        this.currentView = DemographicsEditor.view.loading;
        this.rootEngagementId = Number(this.rootElement.dataset.rootId);
        this.assertRootEntry();
        this.binder = new GravyBinder(this, this.rootElement);
        this.loader = new MDCLinearProgress(this.rootElement.querySelector('.mdc-linear-progress'));
        this.loader.determinate = false;
        this.loader.open();
        this.binder.updateOutwardBindings();

        const options = await this.service.getOptions(this.rootEngagementId);

        this.applyOptions(options);

        this.assertRootEntry();
        this.binder.updateOutwardBindings();

        this.currentView = options.find(o => (o.id !== this.rootEngagementId && o.isSelected) || (o.id === this.rootEngagementId && o.notes)) ? DemographicsEditor.view.overview : DemographicsEditor.view.editor;

        this.binder.updateOutwardBindings();
        this.loader.close();
    }

    private applyOptions = (options: Array<DemographicSetting>): void => {
        for (let i = 0; i < options.length; i++) {
            const opt = options[i];
            this.options[opt.id] = opt;
        }
        this.assertRootEntry();
        this.binder.updateOutwardBindings();
    };


    public saveChanges = async (): Promise<void> => {
        this.loader.open();
        await this.service.setOptions(this.getPayload());

        this.loader.close();
        this.isSaveComplete = true;
        this.binder.updateOutwardBindings();
    };

    private getPayload = (): Array<DemographicSetting> => Object.keys(this.options)
        .map(k => this.options[k])
        .map(DemographicSetting.fromJson)
        .map(d => {
            if (d.id === this.rootEngagementId && !d.notes) {
                d.isSelected = false;
            }
            return d;
        })

    private bindEvents = (): void => {
        this.rootElement.addEventListener('submit', event => event.preventDefault());
        loopThroughElements(this.rootElement.getElementsByClassName('demographic-manager__edit'), button => {
            button.addEventListener('click', () => {
                this.currentView = DemographicsEditor.view.editor;
                this.binder.updateOutwardBindings();
            })
        });
        loopThroughElements(this.rootElement.getElementsByClassName('demographic-manager__save'), button => {
            this.currentView = DemographicsEditor.view.overview;
            button.addEventListener('click', this.saveChanges);
        });
        loopThroughElements(this.rootElement.querySelectorAll('md-checkbox[type="checkbox"][data-for]'), (chk: HTMLInputElement) => {
            chk.addEventListener('change', (event: InputEvent): void => {
                const id = Number(chk.dataset.for);

                this.options[id].isSelected = chk.checked;

                if (!chk.checked) {
                    const childrenElement = this.rootElement.querySelector(`div[data-parent="${chk.dataset.for}"]`);
                    if (childrenElement) {
                        loopThroughElements(childrenElement.querySelectorAll('md-checkbox[type="checkbox"][data-for]'), (child: HTMLInputElement) => {
                            child.checked = false;
                            const childId = Number(child.dataset.for);
                            this.options[childId].isSelected = false;
                        });
                    }
                }

                this.isSaveComplete = false;
                this.binder.updateOutwardBindings();
            });
        });
    };

    private assertRootEntry = (): void => {
        if (!this.options[this.rootEngagementId]) {
            this.options[this.rootEngagementId] = new DemographicSetting();
        }
        this.options[this.rootEngagementId].isSelected = true;
    }
}
